import { authService } from '@/auth/auth.service';
import { useAuth } from '@/auth/useAuth';
import { useStoreGuid } from '@/lib/hooks';
import storeService from '@/store/store.service';
import { commonTranslatorConfig } from '@/translations/commonTranslatorConfig';
import getProvidedArgs from '@/translations/getProvidedArgs';
import { browserLocale, TranslationContextProvider } from '@/translations/translator';
import { useBrand } from '@whiz-cart/ui-shared/brand/brandContext.tsx';
import { useKeyDown } from '@whiz-cart/ui-shared/hooks/useKeyDown';
import { ReactNode, useEffect, useState } from 'react';

export interface TranslationContextProps {
    children?: ReactNode;
}

const TRANSLATION_CONTEXT_DEBUG_OPTIONS = {
    key: true,
    variables: true,
};

export default function TranslationContext({ children }: TranslationContextProps) {
    const locale = authService.loginState.useState((s) => s?.locale) ?? browserLocale;
    const brand = useBrand();
    const provideArgs = getProvidedArgs(brand);

    const { storeGuid } = useStoreGuid();
    const [storeConfig] = storeService.getConfig.useAction(storeGuid, { dormant: !storeGuid });

    const { hasRole } = useAuth();
    const isAdminOrPentland = hasRole(['admin', 'pentland'], 'global');
    const areKeysPressed = useKeyDown({ key: 'l', modifiers: ['ctrl'] });
    const isDebugEnabled = isAdminOrPentland && areKeysPressed;

    // Since keydown and keyup events are completely broken on Mac, we have to use a separate toggle
    const [isDebugReallyEnabled, setIsDebugReallyEnabled] = useState(false);

    useEffect(() => {
        if (isDebugEnabled) {
            setIsDebugReallyEnabled((isEnabled) => !isEnabled);
        }
    }, [isDebugEnabled]);

    return (
        <TranslationContextProvider
            locale={locale}
            options={{
                provideArgs,
                fallbackLocale: storeConfig?.fallbackLanguage ?? commonTranslatorConfig.fallbackLocale,
                debug: isDebugReallyEnabled ? TRANSLATION_CONTEXT_DEBUG_OPTIONS : false,
            }}
        >
            {children}
        </TranslationContextProvider>
    );
}
